import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { userAPI, gamesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { 
    ArrowLeft, CalendarDays, MapPin, Clock, Users, 
    Gamepad2, Sword, Sparkles, ChevronLeft, ChevronRight
} from 'lucide-react';

const CalendarPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [myGames, setMyGames] = useState([]);
    const [allGames, setAllGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDayGames, setSelectedDayGames] = useState([]);
    const [showDayModal, setShowDayModal] = useState(false);

    // Fetch games
    useEffect(() => {
        const fetchGames = async () => {
            try {
                setLoading(true);
                const [myGamesRes, allGamesRes] = await Promise.all([
                    userAPI.getMyGames(),
                    gamesAPI.getGames({ status: 'upcoming' })
                ]);
                setMyGames(myGamesRes.data || []);
                setAllGames(allGamesRes.data || []);
            } catch (error) {
                console.error('Error fetching games:', error);
                toast.error('Failed to load games');
            } finally {
                setLoading(false);
            }
        };

        fetchGames();
    }, []);

    // Get games for a specific date
    const getGamesForDate = (date) => {
        return allGames.filter(game => {
            const gameDate = new Date(game.date_time);
            return isSameDay(gameDate, date);
        });
    };

    // Dates with games (for highlighting in calendar)
    const datesWithGames = useMemo(() => {
        const dates = new Set();
        allGames.forEach(game => {
            const gameDate = new Date(game.date_time);
            dates.add(format(gameDate, 'yyyy-MM-dd'));
        });
        return dates;
    }, [allGames]);

    // Dates with MY games (for special highlighting)
    const datesWithMyGames = useMemo(() => {
        const dates = new Set();
        myGames.forEach(game => {
            const gameDate = new Date(game.date_time);
            dates.add(format(gameDate, 'yyyy-MM-dd'));
        });
        return dates;
    }, [myGames]);

    // Handle date selection
    const handleDateSelect = (date) => {
        if (!date) return;
        setSelectedDate(date);
        const games = getGamesForDate(date);
        if (games.length > 0) {
            setSelectedDayGames(games);
            setShowDayModal(true);
        }
    };

    // Custom day content for calendar
    const modifiers = {
        hasGames: (date) => datesWithGames.has(format(date, 'yyyy-MM-dd')),
        hasMyGames: (date) => datesWithMyGames.has(format(date, 'yyyy-MM-dd')),
    };

    const modifiersStyles = {
        hasGames: {
            backgroundColor: 'rgba(0, 255, 65, 0.2)',
            borderRadius: '0',
        },
        hasMyGames: {
            backgroundColor: 'rgba(0, 255, 65, 0.5)',
            border: '2px solid #00ff41',
            borderRadius: '0',
        },
    };

    const gameTypeIcons = {
        mtg: <Sparkles className="w-4 h-4" />,
        dnd: <Sword className="w-4 h-4" />,
        board_game: <Gamepad2 className="w-4 h-4" />
    };

    // Upcoming games list
    const upcomingGames = useMemo(() => {
        const now = new Date();
        return allGames
            .filter(game => new Date(game.date_time) >= now)
            .sort((a, b) => new Date(a.date_time) - new Date(b.date_time))
            .slice(0, 10);
    }, [allGames]);

    if (!user) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="matrix-loader" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="calendar-page">
            <div className="scanlines" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 border-b border-[#003b00] backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-[#00ff41]"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-[#00ff41] tracking-wider flex items-center gap-2">
                            <CalendarDays className="w-5 h-5" />
                            GAME_CALENDAR
                        </h1>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 pb-10 px-4">
                <div className="container mx-auto max-w-6xl">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="matrix-loader" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Calendar */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="lg:col-span-2 terminal-window"
                            >
                                <div className="terminal-header">
                                    <div className="terminal-dot red" />
                                    <div className="terminal-dot yellow" />
                                    <div className="terminal-dot green" />
                                    <span className="ml-4 text-[#00ff41] text-sm">calendar_view.exe</span>
                                </div>
                                
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-2xl font-bold text-[#00ff41]">
                                            {format(currentMonth, 'MMMM yyyy')}
                                        </h2>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                                                className="border-[#003b00] text-[#00ff41]"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentMonth(new Date())}
                                                className="border-[#003b00] text-[#00ff41]"
                                            >
                                                Today
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                                                className="border-[#003b00] text-[#00ff41]"
                                            >
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={handleDateSelect}
                                        month={currentMonth}
                                        onMonthChange={setCurrentMonth}
                                        modifiers={modifiers}
                                        modifiersStyles={modifiersStyles}
                                        className="w-full text-[#00ff41]"
                                        classNames={{
                                            months: "flex flex-col",
                                            month: "space-y-4 w-full",
                                            caption: "hidden",
                                            table: "w-full border-collapse",
                                            head_row: "flex w-full",
                                            head_cell: "text-[#008f11] w-full font-normal text-sm py-2",
                                            row: "flex w-full mt-1",
                                            cell: "w-full p-0 text-center relative",
                                            day: "w-full h-12 p-0 font-normal text-[#00ff41] hover:bg-[#003b00] transition-colors flex items-center justify-center border border-[#003b00]/30",
                                            day_selected: "bg-[#00ff41] text-black hover:bg-[#00ff41]",
                                            day_today: "border-[#00ff41] border-2",
                                            day_outside: "text-[#003b00] opacity-50",
                                            day_disabled: "text-[#003b00] opacity-30",
                                        }}
                                    />

                                    {/* Legend */}
                                    <div className="mt-6 flex flex-wrap gap-4 text-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 bg-[rgba(0,255,65,0.2)] border border-[#003b00]" />
                                            <span className="text-[#008f11]">Games available</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 bg-[rgba(0,255,65,0.5)] border-2 border-[#00ff41]" />
                                            <span className="text-[#008f11]">Your games</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border-2 border-[#00ff41]" />
                                            <span className="text-[#008f11]">Today</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Upcoming Games Sidebar */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 }}
                                className="terminal-window"
                            >
                                <div className="terminal-header">
                                    <div className="terminal-dot red" />
                                    <div className="terminal-dot yellow" />
                                    <div className="terminal-dot green" />
                                    <span className="ml-4 text-[#00ff41] text-sm">upcoming.log</span>
                                </div>
                                
                                <div className="p-4">
                                    <h3 className="text-lg font-bold text-[#00ff41] mb-4">
                                        UPCOMING_GAMES
                                    </h3>
                                    
                                    <ScrollArea className="h-[500px]">
                                        {upcomingGames.length > 0 ? (
                                            <div className="space-y-3">
                                                {upcomingGames.map((game) => {
                                                    const isMyGame = myGames.some(g => g.game_id === game.game_id);
                                                    return (
                                                        <div 
                                                            key={game.game_id}
                                                            className={`p-3 border cursor-pointer hover:bg-[#003b00]/50 transition-colors ${
                                                                isMyGame 
                                                                    ? 'border-[#00ff41] bg-[#00ff41]/10' 
                                                                    : 'border-[#003b00]'
                                                            }`}
                                                            onClick={() => navigate('/dashboard')}
                                                        >
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-[#00ff41]">
                                                                    {gameTypeIcons[game.game_type] || <Gamepad2 className="w-4 h-4" />}
                                                                </span>
                                                                <span className="text-[#00ff41] font-bold text-sm truncate">
                                                                    {game.title}
                                                                </span>
                                                            </div>
                                                            <div className="space-y-1 text-xs text-[#008f11]">
                                                                <div className="flex items-center gap-1">
                                                                    <CalendarDays className="w-3 h-3" />
                                                                    {format(new Date(game.date_time), 'MMM d, h:mm a')}
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <Users className="w-3 h-3" />
                                                                    {game.current_players?.length || 0}/{game.max_players}
                                                                </div>
                                                            </div>
                                                            {isMyGame && (
                                                                <span className="inline-block mt-2 px-2 py-0.5 bg-[#00ff41] text-black text-xs">
                                                                    JOINED
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <CalendarDays className="w-8 h-8 text-[#003b00] mx-auto mb-2" />
                                                <p className="text-[#008f11] text-sm">No upcoming games</p>
                                            </div>
                                        )}
                                    </ScrollArea>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </div>
            </main>

            {/* Day Games Modal */}
            <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
                <DialogContent className="bg-[#0a0a0a] border-[#003b00] text-[#00ff41] max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl tracking-wider">
                            GAMES_ON_{selectedDate && format(selectedDate, 'MMM_d')}
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh]">
                        <div className="space-y-4">
                            {selectedDayGames.map((game) => {
                                const isMyGame = myGames.some(g => g.game_id === game.game_id);
                                return (
                                    <div 
                                        key={game.game_id}
                                        className={`p-4 border ${
                                            isMyGame ? 'border-[#00ff41]' : 'border-[#003b00]'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#00ff41]">
                                                    {gameTypeIcons[game.game_type] || <Gamepad2 className="w-4 h-4" />}
                                                </span>
                                                <span className="font-bold">{game.title}</span>
                                            </div>
                                            {isMyGame && (
                                                <span className="px-2 py-0.5 bg-[#00ff41] text-black text-xs">
                                                    JOINED
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="space-y-1 text-sm text-[#008f11]">
                                            <p>{game.game_name}</p>
                                            <div className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {format(new Date(game.date_time), 'h:mm a')} • {game.duration_hours}h
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Users className="w-3 h-3" />
                                                {game.current_players?.length || 0}/{game.max_players} players
                                            </div>
                                            {game.address && (
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    <span className="truncate">{game.address}</span>
                                                </div>
                                            )}
                                        </div>

                                        <Button
                                            size="sm"
                                            className="mt-3 w-full bg-[#00ff41] text-black"
                                            onClick={() => navigate('/dashboard')}
                                        >
                                            View on Map
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CalendarPage;
